/**
 * Sentry — carga diferida, scrubbing estricto y cero impacto en el arranque.
 *
 * El SDK NO se importa de forma estática: se carga en `requestIdleCallback`
 * después del primer render, así el bundle inicial no crece de forma
 * perceptible. Si Sentry está caído, lento o el DSN no está configurado, la
 * app funciona exactamente igual (todo va envuelto en try/catch).
 *
 * El DSN vive como secreto del backend y lo entrega la edge function pública
 * `os-sentry-config` (el DSN es público por diseño, pero así no queda
 * hardcodeado en el repo y se puede rotar sin redeploy del frontend).
 */

type SentryModule = typeof import("@sentry/react");

let SentryRef: SentryModule | null = null;
let initPromise: Promise<SentryModule | null> | null = null;

const DSN_CACHE_KEY = "sc.sentry.cfg";

export const APP_RELEASE: string =
  (typeof __APP_RELEASE__ !== "undefined" ? __APP_RELEASE__ : "dev") || "dev";

function currentEnvironment(): string {
  if (typeof window === "undefined") return "unknown";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host.includes("id-preview") || host.includes("lovableproject.com")) return "preview";
  return "production";
}

/** Ruido conocido que nunca queremos en Sentry (ni gastando cuota ni atención). */
const IGNORE_ERRORS = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "AbortError",
  "NetworkError when attempting to fetch resource",
  "Load failed",
  "TypeError: Failed to fetch",
  "top.GLOBALS",
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
];

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /googletagmanager\.com/i,
];

/** Claves cuyo valor jamás debe salir del navegador. */
const SECRET_KEY_RE =
  /(authorization|auth[-_]?token|access[-_]?token|refresh[-_]?token|id[-_]?token|api[-_]?key|apikey|secret|password|passwd|pwd|jwt|bearer|cookie|session|credential|ciphertext|plaintext|private[-_]?key|otp|code_verifier)/i;

/** Patrones de secretos embebidos en strings libres. */
const SECRET_VALUE_PATTERNS: RegExp[] = [
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, // JWT
  /sb-[a-z0-9-]+-auth-token/gi,
  /(sk|pk|sntrys|sntryu)_[A-Za-z0-9_-]{12,}/g,
  /Bearer\s+[A-Za-z0-9._-]{12,}/gi,
];

function scrubString(input: string): string {
  let out = input;
  for (const re of SECRET_VALUE_PATTERNS) out = out.replace(re, "[redacted]");
  return out;
}

const MAX_DEPTH = 6;

function scrubValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return "[truncated]";
  if (typeof value === "string") return scrubString(value);
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => scrubValue(v, depth + 1));

  const src = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(src)) {
    if (SECRET_KEY_RE.test(key)) {
      out[key] = "[redacted]";
      continue;
    }
    out[key] = scrubValue(src[key], depth + 1);
  }
  return out;
}

/** Saneado final del evento antes del envío. */
function scrubEvent(event: any): any {
  try {
    if (event.request) {
      // Nunca enviamos cuerpos de request ni cookies/headers.
      delete event.request.cookies;
      delete event.request.data;
      if (event.request.headers) event.request.headers = "[redacted]";
      if (typeof event.request.url === "string") {
        event.request.url = scrubString(event.request.url.split("#")[0]);
      }
    }
    if (event.extra) event.extra = scrubValue(event.extra);
    if (event.contexts) event.contexts = scrubValue(event.contexts);
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((b: any) => ({
        ...b,
        message: typeof b.message === "string" ? scrubString(b.message) : b.message,
        data: b.data ? scrubValue(b.data) : b.data,
      }));
    }
    if (event.message) event.message = scrubString(String(event.message));
    if (event.exception?.values) {
      for (const v of event.exception.values) {
        if (v?.value) v.value = scrubString(String(v.value));
      }
    }
    // Solo el email del usuario afectado (decisión explícita); nada más de PII.
    if (event.user) {
      event.user = {
        id: event.user.id,
        email: event.user.email,
        role: (event.user as any).role,
      };
    }
  } catch {
    /* si el scrubbing falla, mejor no enviar nada */
    return null;
  }
  return event;
}

async function fetchConfig(): Promise<{ dsn: string; tracesSampleRate: number } | null> {
  try {
    const cached = sessionStorage.getItem(DSN_CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch {
    /* ignore */
  }
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/os-sentry-config`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(url, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string },
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const json = await res.json();
    if (!json?.dsn) return null;
    try {
      sessionStorage.setItem(DSN_CACHE_KEY, JSON.stringify(json));
    } catch {
      /* ignore */
    }
    return json;
  } catch {
    return null;
  }
}

/** Arranca el SDK una sola vez. Seguro de llamar múltiples veces. */
export function initMonitoring(): Promise<SentryModule | null> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    if (typeof window === "undefined") return null;
    const cfg = await fetchConfig();
    if (!cfg?.dsn) return null;

    const Sentry = await import("@sentry/react");
    const environment = currentEnvironment();

    Sentry.init({
      dsn: cfg.dsn,
      environment,
      release: APP_RELEASE,
      // Nunca PII automática: el email lo mandamos nosotros explícitamente.
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      // Performance / Web Vitals con muestreo bajo (no afecta desempeño).
      tracesSampleRate:
        typeof cfg.tracesSampleRate === "number"
          ? cfg.tracesSampleRate
          : environment === "production"
            ? 0.1
            : 1.0,
      tracePropagationTargets: [/^\//, /supabase\.co\/functions\//],
      // Replay: 0% de sesiones normales, 100% de las sesiones con error.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      maxBreadcrumbs: 60,
      beforeSend: (event) => scrubEvent(event),
      beforeBreadcrumb: (crumb) => {
        // Los clics en inputs pueden filtrar texto → fuera.
        if (crumb.category === "ui.input") return null;
        if (crumb.data) crumb.data = scrubValue(crumb.data) as any;
        return crumb;
      },
    });

    Sentry.setTag("app", "smart-canvas-os");
    SentryRef = Sentry;
    return Sentry;
  })().catch(() => null);
  return initPromise;
}

/** Arranca cuando el navegador esté ocioso: cero impacto en el first paint. */
export function scheduleMonitoring() {
  if (typeof window === "undefined") return;
  const start = () => {
    void initMonitoring();
  };
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(start, { timeout: 5000 });
  } else {
    setTimeout(start, 2500);
  }
}

export function setMonitoringUser(u: { id: string; email?: string | null; role?: string | null } | null) {
  void initMonitoring().then((S) => {
    if (!S) return;
    try {
      if (!u) {
        S.setUser(null);
        return;
      }
      S.setUser({ id: u.id, email: u.email ?? undefined, role: u.role ?? undefined } as any);
      if (u.role) S.setTag("role", u.role);
    } catch {
      /* ignore */
    }
  });
}

export function setMonitoringContext(key: string, value: Record<string, unknown> | null) {
  void initMonitoring().then((S) => {
    if (!S) return;
    try {
      S.setContext(key, value ? (scrubValue(value) as any) : null);
    } catch {
      /* ignore */
    }
  });
}

export function setMonitoringTag(key: string, value: string | undefined) {
  void initMonitoring().then((S) => {
    if (!S) return;
    try {
      S.setTag(key, value);
    } catch {
      /* ignore */
    }
  });
}

export function addMonitoringBreadcrumb(crumb: {
  category: string;
  message: string;
  level?: "info" | "warning" | "error";
  data?: Record<string, unknown>;
}) {
  try {
    SentryRef?.addBreadcrumb({
      category: crumb.category,
      message: scrubString(crumb.message).slice(0, 300),
      level: crumb.level ?? "info",
      data: crumb.data ? (scrubValue(crumb.data) as any) : undefined,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Captura un error y devuelve el Event ID para mostrarlo al usuario
 * ("Código de incidente"). Si el SDK aún no cargó, devuelve null.
 */
export function captureAppError(
  error: unknown,
  ctx?: { area?: string; extra?: Record<string, unknown>; level?: "error" | "fatal" | "warning" },
): string | null {
  try {
    if (!SentryRef) {
      void initMonitoring().then((S) => {
        try {
          S?.captureException(error);
        } catch {
          /* ignore */
        }
      });
      return null;
    }
    return SentryRef.withScope((scope) => {
      if (ctx?.area) scope.setTag("area", ctx.area);
      if (ctx?.level) scope.setLevel(ctx.level);
      if (ctx?.extra) scope.setContext("detalle", scrubValue(ctx.extra) as any);
      return SentryRef!.captureException(error);
    });
  } catch {
    return null;
  }
}

/** Trace header para propagar el mismo trace-id al backend. */
export function monitoringTraceHeaders(): Record<string, string> {
  try {
    if (!SentryRef) return {};
    const headers: Record<string, string> = {};
    const traceData = (SentryRef as any).getTraceData?.();
    if (traceData?.["sentry-trace"]) headers["sentry-trace"] = traceData["sentry-trace"];
    if (traceData?.baggage) headers["baggage"] = traceData.baggage;
    return headers;
  } catch {
    return {};
  }
}
